if (!customElements.get('byob-drawer')) {
  customElements.define(
    'byob-drawer',
    class ByobDrawer extends HTMLElement {
      constructor() {
        super();
        this.selected = { jars: new Map(), snacks: new Map() };
      }

      connectedCallback() {
        this.maxJars = parseInt(this.dataset.maxJars) || 2;
        this.maxSnacks = parseInt(this.dataset.maxSnacks) || 2;
        this.hamperVariantId = this.dataset.hamperVariantId || null;
        this.baseSpreadPrice = parseInt(this.dataset.baseSpreadPrice) || 59900;
        this.baseSnackPrice = parseInt(this.dataset.baseSnackPrice) || 13900;
        this.hamperBasePrice = parseInt(this.dataset.hamperBasePrice) || 39900;
        this.bundlePrice = parseInt(this.dataset.bundlePrice) || 159900;

        this.overlay = this.querySelector('[data-byob-overlay]');
        this.panel = this.querySelector('[data-byob-panel]');
        this.closeBtn = this.querySelector('[data-byob-close]');
        this.submitBtn = this.querySelector('[data-byob-submit]');
        this.summaryCount = this.querySelector('[data-summary-count]');
        this.summaryOriginal = this.querySelector('[data-summary-original]');
        this.summaryTotal = this.querySelector('[data-summary-total]');
        this.summarySavings = this.querySelector('[data-summary-savings]');
        this.jarsCounter = this.querySelector('[data-counter="jars"]');
        this.snacksCounter = this.querySelector('[data-counter="snacks"]');
        this.rows = this.querySelectorAll('.byob-row');
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindEvents();
        this.initRowDifferentials();

        document.body.classList.add('byob-active');

        // Auto-open cart drawer after BYOB add-to-cart reload
        const params = new URLSearchParams(window.location.search);
        if (params.has('byob_added')) {
          params.delete('byob_added');
          const cleanUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
          history.replaceState(null, '', cleanUrl);
          if (this.cart && this.cart.open) {
            setTimeout(() => this.cart.open(), 300);
          }
        }

        const triggerBtn = document.querySelector('[data-byob-open]');
        if (triggerBtn) {
          triggerBtn.addEventListener('click', () => this.open());

          const buyBtnWrapper = document.querySelector('.ct-buy_button');
          if (buyBtnWrapper) {
            buyBtnWrapper.prepend(triggerBtn);
            triggerBtn.classList.add('byob-trigger-btn--inline');
          }
        }
      }

      bindEvents() {
        this.overlay.addEventListener('click', () => this.close());
        this.closeBtn.addEventListener('click', () => this.close());
        this.submitBtn.addEventListener('click', () => this.addToCart());

        this.rows.forEach((row) => {
          const minusBtn = row.querySelector('[data-action="minus"]');
          const plusBtn = row.querySelector('[data-action="plus"]');

          plusBtn.addEventListener('click', () => this.changeQty(row, 1));
          minusBtn.addEventListener('click', () => this.changeQty(row, -1));
        });

        this.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') this.close();
        });
      }

      open() {
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => {
          this.classList.add('byob-drawer--active');
        });
      }

      close() {
        this.classList.remove('byob-drawer--active');
        this.panel.addEventListener(
          'transitionend',
          () => {
            this.removeAttribute('open');
            document.body.classList.remove('overflow-hidden');
          },
          { once: true }
        );
      }

      changeQty(row, delta) {
        const category = row.dataset.category;
        const variantId = row.dataset.variantId;
        const map = this.selected[category];
        const max = category === 'jars' ? this.maxJars : this.maxSnacks;

        const current = map.has(variantId) ? map.get(variantId).qty : 0;
        const categoryTotal = this.getCategoryTotal(category);
        let newQty = current + delta;

        if (newQty < 0) newQty = 0;
        if (delta > 0 && categoryTotal >= max) return;

        if (newQty === 0) {
          map.delete(variantId);
        } else {
          map.set(variantId, {
            title: row.dataset.productTitle,
            price: parseInt(row.dataset.price),
            qty: newQty,
          });
        }

        const qtyDisplay = row.querySelector('[data-qty]');
        const minusBtn = row.querySelector('[data-action="minus"]');
        qtyDisplay.textContent = newQty;
        minusBtn.disabled = newQty === 0;
        row.classList.toggle('byob-row--active', newQty > 0);

        this.updateUI(category);
      }

      updateRowDifferential(row, category) {
        const diffEl = row.querySelector('[data-row-differential]');
        if (!diffEl) return;

        const itemPrice = parseInt(row.dataset.price);
        const basePrice = category === 'jars' ? this.baseSpreadPrice : this.baseSnackPrice;
        const diff = itemPrice - basePrice;

        if (diff > 0) {
          diffEl.textContent = '+' + this.formatMoney(diff);
          diffEl.style.display = '';
        } else {
          diffEl.textContent = '';
          diffEl.style.display = 'none';
        }
      }

      initRowDifferentials() {
        this.rows.forEach((row) => {
          this.updateRowDifferential(row, row.dataset.category);
        });
      }

      getCategoryTotal(category) {
        let total = 0;
        this.selected[category].forEach((v) => (total += v.qty));
        return total;
      }

      getBundlePrice() {
        let differential = 0;
        this.selected.jars.forEach((v) => {
          differential += Math.max(0, v.price - this.baseSpreadPrice) * v.qty;
        });
        this.selected.snacks.forEach((v) => {
          differential += Math.max(0, v.price - this.baseSnackPrice) * v.qty;
        });

        const originalTotal =
          this.hamperBasePrice +
          this.baseSpreadPrice * this.maxJars +
          this.baseSnackPrice * this.maxSnacks +
          differential;
        const discountedTotal = this.bundlePrice + differential;
        const savings = originalTotal - discountedTotal;

        return { originalTotal, discountedTotal, savings, differential };
      }

      updateUI(category) {
        const max = category === 'jars' ? this.maxJars : this.maxSnacks;
        const counter = category === 'jars' ? this.jarsCounter : this.snacksCounter;
        const catTotal = this.getCategoryTotal(category);
        const atLimit = catTotal >= max;

        counter.textContent = `${catTotal} / ${max}`;
        counter.classList.toggle('byob-drawer__step-counter--done', atLimit);

        this.querySelectorAll(`.byob-row[data-category="${category}"]`).forEach((row) => {
          const plusBtn = row.querySelector('[data-action="plus"]');
          plusBtn.disabled = atLimit;
        });

        if (category === 'jars' && atLimit) {
          const snacksStep = this.querySelector('[data-step="snacks"]');
          if (snacksStep) {
            setTimeout(() => {
              snacksStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
          }
        }

        const jarsTotal = this.getCategoryTotal('jars');
        const snacksTotal = this.getCategoryTotal('snacks');
        const totalItems = jarsTotal + snacksTotal;
        const totalRequired = this.maxJars + this.maxSnacks;
        const isComplete = jarsTotal >= this.maxJars && snacksTotal >= this.maxSnacks;

        this.summaryCount.textContent = isComplete
          ? `${totalItems} items`
          : `${totalItems} / ${totalRequired} selected`;

        const { originalTotal, discountedTotal, savings } = this.getBundlePrice();

        if (totalItems > 0) {
          this.summaryOriginal.textContent = this.formatMoney(originalTotal);
          this.summaryOriginal.style.display = '';
          this.summaryTotal.textContent = this.formatMoney(discountedTotal);
          if (this.summarySavings) {
            this.summarySavings.textContent = 'Save ' + this.formatMoney(savings);
            this.summarySavings.style.display = '';
          }
        } else {
          this.summaryOriginal.textContent = '';
          this.summaryOriginal.style.display = 'none';
          this.summaryTotal.textContent = '';
          if (this.summarySavings) {
            this.summarySavings.textContent = '';
            this.summarySavings.style.display = 'none';
          }
        }

        this.submitBtn.disabled = !isComplete;
        this.submitBtn.classList.toggle('byob-drawer__cta--ready', isComplete);
      }

      formatMoney(paise) {
        return '\u20B9' + (paise / 100).toLocaleString('en-IN');
      }

      async addToCart() {
        if (this.submitBtn.disabled) return;
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('loading');
        const spinner = this.submitBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const items = [];

        if (this.hamperVariantId) {
          items.push({
            id: parseInt(this.hamperVariantId),
            quantity: 1,
            properties: { _byob_bundle: 'shark-tank-hamper' },
          });
        }

        const allMaps = [this.selected.jars, this.selected.snacks];
        allMaps.forEach((map) => {
          map.forEach((val, variantId) => {
            items.push({
              id: parseInt(variantId),
              quantity: val.qty,
              properties: { _byob_bundle: 'shark-tank-hamper' },
            });
          });
        });

        try {
          const res = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });

          const data = await res.json();
          if (data.status) {
            console.error('BYOB cart error:', data.description);
            return;
          }

          this.resetSelections();

          // Reload page with flag to auto-open cart drawer
          const url = new URL(window.location.href);
          url.searchParams.set('byob_added', '1');
          window.location.href = url.toString();
        } catch (e) {
          console.error('BYOB add to cart failed:', e);
        } finally {
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      resetSelections() {
        this.selected.jars.clear();
        this.selected.snacks.clear();
        this.querySelectorAll('.byob-row').forEach((row) => {
          row.classList.remove('byob-row--active');
          const qtyDisplay = row.querySelector('[data-qty]');
          const minusBtn = row.querySelector('[data-action="minus"]');
          if (qtyDisplay) qtyDisplay.textContent = '0';
          if (minusBtn) minusBtn.disabled = true;
        });
        this.updateUI('jars');
        this.updateUI('snacks');
      }
    }
  );
}
